import axios from 'axios';
import { supabase } from './supabaseClient';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
});

/**
 * Gets a CloudFront URL for a document
 * @param {string} bucket - The storage bucket name
 * @param {string} path - The path to the file
 * @returns {Promise<string>} The CloudFront URL
 */
export const getDocumentUrl = async (bucket, path) => {
  try {
    // In development, just return the direct Supabase URL
    if (process.env.NODE_ENV === 'development') {
      const { data: signedData, error: signError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 3600);
      
      if (signError) throw signError;
      return signedData.signedUrl;
    }

    // Production CloudFront logic
    if (path.includes(process.env.REACT_APP_CLOUDFRONT_DOMAIN)) {
      return path;
    }

    const { data: signedData, error: signError } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600);

    if (signError) throw signError;

    return signedData.signedUrl.replace(
      `${process.env.REACT_APP_SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/sign`,
      process.env.REACT_APP_CLOUDFRONT_DOMAIN
    );
  } catch (error) {
    console.error('Error getting document URL:', error);
    throw error;
  }
};

/**
 * Invalidates the CloudFront cache for a given path
 * @param {string} filePath - The path to invalidate
 * @returns {Promise<Object>} The invalidation response
 */
export const invalidateCache = async (filePath) => {
  try {
    // Clean the file path (remove any leading slashes)
    const cleanPath = filePath.replace(/^\/+/, '');
    
    console.log('Sending invalidation request for path:', cleanPath);
    
    // Send invalidation request to backend
    const response = await api.post('/invalidate-cache/', {
      paths: [`/${cleanPath}*`]
    });
    
    console.log('Cache invalidation response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to invalidate cache:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Tests the AWS configuration
 * @returns {Promise<Object>} The configuration test results
 */
export const testAwsConfig = async () => {
  try {
    const response = await api.get('/api/v1/test-aws-config/');
    console.log('AWS config test response:', response.data);
    return response.data;
  } catch (error) {
    console.error('AWS config test failed:', error);
    throw error;
  }
};

/**
 * Converts a Supabase URL to a CloudFront URL
 * @param {string} supabaseUrl - The original Supabase URL
 * @returns {string} The CloudFront URL
 */
export const convertToCloudFrontUrl = (supabaseUrl) => {
  if (!supabaseUrl) return null;
  if (process.env.NODE_ENV === 'development') return supabaseUrl;
  if (supabaseUrl.includes(process.env.REACT_APP_CLOUDFRONT_DOMAIN)) return supabaseUrl;

  return supabaseUrl.replace(
    `${process.env.REACT_APP_SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/sign`,
    process.env.REACT_APP_CLOUDFRONT_DOMAIN
  );
};